// src/components/pages/mypage/hooks/useUserProfile.js

import { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import imageCompression from 'browser-image-compression';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export default function useUserProfile(auth, t) {
  const [nickname, setNickname] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showEditNicknameModal, setShowEditNicknameModal] = useState(false);
  const [showEditIntroductionModal, setShowEditIntroductionModal] = useState(false);
  
  const nicknameUrl = `${API_URL}/user/nickname/me`;

  // 사용자 프로필 로드
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!auth.isAuthenticated) return;
      try {
        const res = await fetch(nicknameUrl, {
          method: 'GET',
          headers: { Authorization: `Bearer ${auth.user?.id_token}` },
        });
        if (!res.ok) {
          if (res.status === 404) {
            setNickname('');
            setIntroduction('');
            setProfileImageUrl('');
          } else {
            throw new Error(`Failed to fetch user profile (${res.status})`);
          }
        } else {
          const data = await res.json();
          setNickname(data.nickname || '');
          setIntroduction(data.introduction || '');
          setProfileImageUrl(data.profileImageUrl || '');
        }
      } catch (err) {
        console.error('Failed to fetch user profile', err);
        // 사용자에게 직접적인 오류 메시지를 표시하는 대신 콘솔에 로깅
      }
    };

    if (auth.isAuthenticated) {
      fetchUserProfile();
    }
  }, [nicknameUrl, auth.isAuthenticated, auth.user?.id_token, t]);

  // 닉네임 업데이트 핸들러
  const handleUpdateNickname = useCallback(async (newName) => {
    setShowEditNicknameModal(false);
    const sanitizedName = DOMPurify.sanitize(newName.trim(), { USE_PROFILES: { html: false } });
    if (!sanitizedName || sanitizedName === nickname) return;
    try {
      const res = await fetch(nicknameUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.user.id_token}`,
        },
        body: JSON.stringify({ nickname: sanitizedName, introduction, profileImageUrl }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = (res.status === 409) ? t('mypage.error.nicknameDuplicate') : errorData.error || t('mypage.error.nicknameUpdateFailed');
        throw new Error(errorMessage);
      }
      setNickname(sanitizedName);
      alert(t('mypage.alert.nicknameUpdated'));
    } catch (err) {
      console.error('Failed to update nickname', err);
      alert(`${t('mypage.error.nicknameUpdateFailed')}: ${err.message}`);
    }
  }, [auth.user?.id_token, introduction, nickname, profileImageUrl, t, nicknameUrl]);

  // 소개 업데이트 핸들러
  const handleUpdateIntroduction = useCallback(async (newIntroduction) => {
    setShowEditIntroductionModal(false);
    const sanitizedIntroduction = DOMPurify.sanitize(newIntroduction.trim());
    if (sanitizedIntroduction === null || sanitizedIntroduction === introduction) return;
    try {
      const res = await fetch(nicknameUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.user.id_token}`,
        },
        body: JSON.stringify({ nickname, introduction: sanitizedIntroduction, profileImageUrl }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('mypage.error.introductionUpdateFailed'));
      }
      setIntroduction(sanitizedIntroduction);
      alert(t('mypage.alert.introductionUpdated'));
    } catch (err) {
      console.error('Failed to update introduction', err);
      alert(`${t('mypage.error.introductionUpdateGeneric')} ${err.message}`);
    }
  }, [auth.user?.id_token, nickname, introduction, profileImageUrl, t, nicknameUrl]);

  // 프로필 이미지 업로드 핸들러
  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert(t('mypage.alert.fileSizeExceeded'));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert(t('mypage.alert.imageFormatUnsupported'));
      return;
    }

    try {
      setLoading(true);
      const options = { maxSizeMB: 0.2, maxWidthOrHeight: 800, useWebWorker: true, fileType: 'image/webp', initialQuality: 0.8 };
      const compressedFile = await imageCompression(file, options);

      // S3 URL 요청
      const getUrlRes = await fetch(`${API_URL}/user/profile-image/me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
        body: JSON.stringify({ fileName: file.name, fileType: 'image/webp' }),
      });
      if (!getUrlRes.ok) throw new Error(t('mypage.error.s3UrlFailed'));
      const { uploadUrl, fileKey } = await getUrlRes.json();
      const publicUrl = `https://${process.env.REACT_APP_PROFILE_IMAGE_S3_BUCKET_NAME}.s3.amazonaws.com/${fileKey}`;

      // S3 업로드
      const uploadRes = await fetch(uploadUrl, { method: 'PUT', body: compressedFile, headers: { 'Content-Type': 'image/webp' } });
      if (!uploadRes.ok) throw new Error(t('mypage.error.s3UploadFailed'));

      // 프로필 정보 업데이트
      const updateProfileRes = await fetch(nicknameUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
        body: JSON.stringify({ nickname, introduction, profileImageUrl: publicUrl }),
      });
      if (!updateProfileRes.ok) throw new Error(t('mypage.error.profileUpdateFailed'));

      setProfileImageUrl(publicUrl);
      alert(t('mypage.alert.profileImageUpdated'));
    } catch (error) {
      console.error('프로필 이미지 업로드 오류:', error);
      alert(`${t('mypage.error.profileImageUpdateGeneric')} ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = () => fileInputRef.current.click();

  return {
    nickname,
    introduction,
    profileImageUrl,
    loading,
    fileInputRef,
    showEditNicknameModal,
    setShowEditNicknameModal,
    showEditIntroductionModal,
    setShowEditIntroductionModal,
    handleImageClick,
    handleProfileImageUpload,
    handleUpdateNickname,
    handleUpdateIntroduction,
  };
}